import React, {useState} from "react"
import {Spinner} from "@heroui/react"
import Badge from "@/components/general/ui/Badge"
import {AlertType, AlertActivationStatus} from "../alertTypes"
import {
  IconDotsVertical,
  IconCircleDashed,
  IconCircleDashedCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react"

import {AlertItem} from "./AlertItem"
import {IconContainer} from "./IconContainer"

interface AlertsListProps {
  unresolvedAlerts: AlertType[]
  resolvedAlerts: AlertType[]
  loading?: boolean
  updatingItems?: Set<string>
  optimisticUpdateFunctions?: {
    setItemUpdating?: (alertId: string, isUpdating: boolean) => void
    updateAlertOptimistically?: (
      alertId: string,
      updates: Partial<AlertType>
    ) => void
    moveAlertBetweenLists?: (
      alertId: string,
      fromStatus: AlertActivationStatus,
      toStatus: AlertActivationStatus,
      updates?: Partial<AlertType>
    ) => void
    refetchInBackground?: () => Promise<void>
  }
}

export const AlertsList: React.FC<AlertsListProps> = ({
  unresolvedAlerts,
  resolvedAlerts,
  loading = false,
  updatingItems,
  optimisticUpdateFunctions,
}) => {
  const [unresolvedOpen, setUnresolvedOpen] = useState(true)
  const [resolvedOpen, setResolvedOpen] = useState(false)

  // Show loading state
  if (loading) {
    return (
      <div className="mt-2 rounded-xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
        <Spinner size="lg" color="primary" />
        <p className="mt-4 text-sm text-neutral-600">Loading alerts...</p>
      </div>
    )
  }

  return (
    <div className="mt-2 rounded-xl border border-neutral-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3">
        <h3 className="text-base font-medium text-neutral-800">
          Alert Activity
        </h3>
        <div className="flex items-center gap-2">
          <button className="rounded-full p-1 hover:bg-neutral-100">
            <IconDotsVertical className="h-4 w-4 text-neutral-500" />
          </button>
        </div>
      </div>

      <div className="rounded-b-xl">
        {/* Unresolved Section */}
        <div className="border-b border-neutral-100">
          <button
            onClick={() => setUnresolvedOpen(!unresolvedOpen)}
            className="flex w-full items-center justify-between bg-neutral-100 px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-200"
          >
            <div className="flex items-center gap-1">
              <IconContainer>
                <IconCircleDashed className="h-4 w-4 text-red-500" />
              </IconContainer>
              <span>Unresolved Issues</span>
              {unresolvedAlerts.length > 0 && (
                <Badge variant="red">{unresolvedAlerts.length}</Badge>
              )}
            </div>
            {unresolvedOpen ? (
              <IconChevronUp className="h-4 w-4 text-neutral-400" />
            ) : (
              <IconChevronDown className="h-4 w-4 text-neutral-400" />
            )}
          </button>

          {unresolvedOpen && (
            <div className="divide-y-1 divide-gray-300">
              {unresolvedAlerts.length > 0 ? (
                unresolvedAlerts.map((alert) => (
                  <AlertItem
                    key={alert.id}
                    alert={alert}
                    isUpdating={updatingItems?.has(alert.id)}
                    optimisticUpdateFunctions={optimisticUpdateFunctions}
                  />
                ))
              ) : (
                <div className="py-6 text-center text-sm text-neutral-500">
                  No unresolved alerts
                </div>
              )}
            </div>
          )}
        </div>

        {/* Resolved Section */}
        <div className="border-t-2 border-gray-300">
          <button
            onClick={() => setResolvedOpen(!resolvedOpen)}
            className="flex w-full items-center justify-between bg-neutral-100 px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-200"
          >
            <div className="flex items-center gap-1">
              <IconContainer>
                <IconCircleDashedCheck className="h-4 w-4 text-green-500" />
              </IconContainer>
              <span>Resolved Issues</span>
              {resolvedAlerts.length > 0 && (
                <Badge variant="green">{resolvedAlerts.length}</Badge>
              )}
            </div>
            {resolvedOpen ? (
              <IconChevronUp className="h-4 w-4 text-neutral-400" />
            ) : (
              <IconChevronDown className="h-4 w-4 text-neutral-400" />
            )}
          </button>

          {resolvedOpen && (
            <div className="divide-y-1 divide-gray-300">
              {resolvedAlerts.length > 0 ? (
                resolvedAlerts.map((alert) => (
                  <AlertItem
                    key={alert.id}
                    alert={alert}
                    isUpdating={updatingItems?.has(alert.id)}
                    optimisticUpdateFunctions={optimisticUpdateFunctions}
                  />
                ))
              ) : (
                <div className="py-6 text-center text-sm text-neutral-500">
                  No resolved alerts
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
